/**
 * @file invoiceTotals.ts
 * @brief Reading the unified invoice feed's aggregate fields correctly.
 *
 * @details
 * - What the file does: Turns `GET /api/invoices`'s per-currency totals into
 *   something a stat card can render, and provides the one window-preset
 *   builder both invoice surfaces share.
 * - Where it fits: `features/company-invoices` and `features/admin-invoices`
 *   render the same three cards from the same endpoint; `features/dashboard`
 *   reads the open balance from it.
 * - Important technical decisions (24-08-2026, from the Usage dashboard audit):
 *   `totalAmountCents` is a currency-BLIND sum — the backend adds every row's
 *   minor units together regardless of currency. Both pages were rendering it
 *   through `formatMoneyMinor(total, "GBP")`, a hard-coded symbol; on the super
 *   admin page that total spans every tenant on the platform, so a single
 *   non-GBP company made the headline wrong rather than merely mislabelled.
 *   `totalsByCurrency` is the correct source. The helpers below degrade to the
 *   flat total when the backend predates 24-08-2026.
 *
 * @references
 * - BACKEND/src/features/invoices/routes.js (GET /, totalsByCurrency)
 * - docs/product/invoices.md §4b
 *
 * @author DES
 * @date 24-08-2026
 */

export type InvoiceCurrencyTotal = {
  currency: string;
  amountCents: number;
  openAmountCents?: number;
  count?: number;
};

export type InvoiceTotalsSource = {
  totalAmountCents?: number | string;
  openAmountCents?: number | string;
  totalsByCurrency?: InvoiceCurrencyTotal[];
};

export type ResolvedInvoiceTotals = {
  /** Amount to show as the headline, in minor units of `currency`. */
  amountCents: number;
  /** Uncaptured portion of the same scope and currency. */
  openAmountCents: number;
  currency: string;
  /** Currencies beyond the primary one — empty in the normal single-currency
   *  case. Render these rather than folding them into the headline. */
  others: InvoiceCurrencyTotal[];
};

const FALLBACK_CURRENCY = "GBP";

function toNumber(value: number | string | undefined | null): number {
  if (value == null) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Resolves a scope's headline total.
 *
 * Prefers the per-currency split (largest first, as the backend orders it) and
 * falls back to the flat sum when the field is absent.
 */
export function resolveInvoiceTotals(
  source: InvoiceTotalsSource | undefined,
  fallbackCurrency = FALLBACK_CURRENCY,
): ResolvedInvoiceTotals {
  const split = source?.totalsByCurrency ?? [];
  if (split.length > 0) {
    const [primary, ...others] = split;
    return {
      amountCents: toNumber(primary.amountCents),
      openAmountCents: toNumber(primary.openAmountCents),
      currency: primary.currency || fallbackCurrency,
      others,
    };
  }
  return {
    amountCents: toNumber(source?.totalAmountCents),
    openAmountCents: toNumber(source?.openAmountCents),
    currency: fallbackCurrency,
    others: [],
  };
}

/** True when the scope spans more than one currency, so the UI must say so. */
export function isMixedCurrency(totals: ResolvedInvoiceTotals): boolean {
  return totals.others.length > 0;
}

// ----------------------------------------------------------------------
// Window presets
// Was duplicated verbatim in company-invoices/schemas.ts and
// admin-invoices/schemas.ts under a comment reading "Kept identical to the
// company-invoices sibling" — the same two-copies-of-one-rule arrangement that
// let the mobile Home card and Usage dashboard drift apart. One copy now.
// Boundaries are LOCAL days sent as full ISO timestamps: a bare 'YYYY-MM-DD' is
// read by the backend as UTC midnight, which shifts the window by the viewer's
// whole UTC offset. (24-08-2026)
// ----------------------------------------------------------------------
export type InvoicePeriod = "mtd" | "3m" | "all";

export function invoicePeriodRange(period: InvoicePeriod): {
  from?: string;
  to?: string;
} {
  if (period === "all") return {};
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const to = end.toISOString();
  const start = new Date();
  if (period === "mtd") {
    start.setDate(1);
  } else {
    start.setDate(start.getDate() - 90);
  }
  start.setHours(0, 0, 0, 0);
  return { from: start.toISOString(), to };
}
